import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { PRACTICE_AREAS } from "../data/practiceAreas";
import ContactForm from "../components/site/ContactForm";

const WHY_POINTS = [
  "Pay-per-lead model — no long-term contracts required",
  "Exclusive delivery — every lead goes to one firm only, never resold",
  "Real-time delivery via email, SMS, or direct CRM integration",
  "Documented TCPA consent on every submission",
  "Targeted by practice area and geography",
  "Replacement or credit for leads that fail intake criteria",
];

const STEPS = [
  {
    n: "1",
    title: "Tell us your practice area and geography",
    body: "Share the practice areas you handle and the regions you want to cover. We build intake criteria around your firm's requirements.",
  },
  {
    n: "2",
    title: "We launch compliant acquisition campaigns",
    body: "Our team runs paid search, social, and owned web properties to attract high-intent prospects, capturing documented consent on every submission.",
  },
  {
    n: "3",
    title: "Exclusive leads are delivered in real time",
    body: "Qualified leads reach your inbox, phone, or CRM the moment they are captured — sent to your firm only, never shared or resold.",
  },
  {
    n: "4",
    title: "You follow up and convert",
    body: "Your intake team engages fresh, screened prospects. Leads that fail agreed intake criteria are eligible for credit or replacement.",
  },
];

const FAQS = [
  {
    q: "What types of legal leads does LegalConnect360 provide?",
    a: "We provide exclusive leads across seven practice areas: Social Security Disability, motor vehicle accidents, mass tort, solar panel fraud, bankruptcy, FICA tip credits, and tariff refunds. Each lead is generated through compliant paid search, social, and owned web campaigns, and is delivered to a single firm or advisory practice in real time.",
  },
  {
    q: "How do you ensure lead quality?",
    a: "Every submission is screened against practice-area-specific intake criteria and carries documented TCPA consent, with TrustedForm certification where applicable. We capture structured intake data so your team can quickly confirm that a prospect meets your requirements before investing follow-up time.",
  },
  {
    q: "How quickly do I receive leads after signing up?",
    a: "Once your campaign is configured and live, leads are delivered in real time — typically within seconds of a prospect completing an intake form. Faster delivery means you can reach the prospect while their intent is highest.",
  },
  {
    q: "How does pricing work?",
    a: "Per-lead pricing varies by practice area and geography. Mass tort is priced per signed retainer rather than per raw lead, aligning cost with claimants who move forward into your case. There are no long-term contracts required.",
  },
  {
    q: "Are leads exclusive?",
    a: "Yes. Every lead is delivered to one firm only. We never share, resell, or recycle leads across multiple buyers.",
  },
  {
    q: "What if a lead doesn't qualify?",
    a: "If a lead fails the intake criteria agreed for your campaign, it is eligible for a credit or replacement. We want your spend going toward prospects that genuinely fit your practice.",
  },
];

function Section({ id, alt, children, className = "" }) {
  return (
    <section
      id={id}
      className={`${alt ? "bg-altbg" : "bg-white"} ${className}`}
    >
      <div className="max-w-content mx-auto px-4 sm:px-6 py-14 sm:py-16">
        {children}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      {/* a) HERO */}
      <section className="bg-white border-b border-hairline" data-testid="hero-section">
        <div className="max-w-content mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="max-w-2xl">
              <p className="text-gold font-semibold text-sm uppercase tracking-wider mb-4">
                Legal Lead Generation
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-6">
                Exclusive Legal Leads for Law Firms and Recovery Practices
              </h1>
              <p className="text-lg text-subink mb-8">
                LegalConnect360 connects law firms and professional practices with
                exclusive, consent-documented leads across seven practice areas:
                Social Security Disability, motor vehicle accidents, mass tort,
                solar panel fraud, bankruptcy, FICA tip credits, and tariff
                refunds. Real-time delivery. No contracts. No shared leads.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/contact"
                  className="inline-block text-center bg-gold text-navy font-semibold px-6 py-3 rounded-[4px] hover:bg-navy hover:text-white transition-colors"
                  data-testid="hero-get-started-btn"
                >
                  Get Started
                </Link>
                <a
                  href="#practice-areas"
                  className="inline-block text-center border border-navy text-navy font-semibold px-6 py-3 rounded-[4px] hover:bg-navy hover:text-white transition-colors"
                  data-testid="hero-view-areas-btn"
                >
                  View Practice Areas
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Legal team reviewing casework in a conference room"
                className="w-full h-[380px] object-cover rounded-[4px] border border-hairline"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* b) WHY FIRMS WORK WITH US */}
      <Section id="why-us" data-testid="why-section">
        <h2 className="text-3xl sm:text-4xl mb-8">Why Firms Work With Us</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 max-w-4xl">
          {WHY_POINTS.map((point) => (
            <li key={point} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <span className="text-ink">{point}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how-it-works" alt data-testid="how-it-works-section">
        <h2 className="text-3xl sm:text-4xl mb-3">How It Works</h2>
        <p className="text-subink max-w-2xl mb-10">
          A straightforward, compliance-first process that gets exclusive leads
          into your intake pipeline quickly.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((step) => (
            <div
              key={step.n}
              className="bg-white border border-hairline rounded-[4px] p-6"
              data-testid={`how-step-${step.n}`}
            >
              <div className="text-gold font-extrabold text-2xl mb-3">
                {step.n}
              </div>
              <h3 className="text-lg mb-2">{step.title}</h3>
              <p className="text-sm text-subink">{step.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* c) PRACTICE AREAS */}
      <Section id="practice-areas" data-testid="practice-areas-section">
        <h2 className="text-3xl sm:text-4xl mb-8">
          Legal Leads Available for These Practice Areas
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PRACTICE_AREAS.map((area) => (
            <Link
              key={area.slug}
              to={`/${area.slug}`}
              className="block bg-white border border-hairline rounded-[4px] p-6 hover:border-navy transition-colors group"
              data-testid={`practice-card-${area.slug}`}
            >
              <h3 className="text-xl mb-1 group-hover:text-gold transition-colors">
                {area.name}
              </h3>
              <p className="text-sm text-subink">{area.cardBlurb}</p>
            </Link>
          ))}
        </div>
      </Section>

      {/* d) REAL-TIME DELIVERY */}
      <Section id="delivery" alt data-testid="delivery-section">
        <h2 className="text-3xl sm:text-4xl mb-4">
          Real-Time Leads Straight to Your Inbox or CRM
        </h2>
        <p className="text-subink max-w-2xl mb-6">
          Every lead is delivered the moment it is captured, so your intake team
          can respond while the prospect's intent is at its peak. Choose the
          delivery method that fits your workflow:
        </p>
        <ul className="space-y-3 max-w-xl mb-6">
          {["Email", "SMS", "Direct CRM integration"].map((d) => (
            <li key={d} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <span className="text-ink">{d}</span>
            </li>
          ))}
        </ul>
        <p className="text-lg font-bold text-navy">
          Faster follow-up = higher conversion.
        </p>
      </Section>

      {/* e) COMPLIANCE */}
      <Section id="compliance" data-testid="compliance-section">
        <h2 className="text-3xl sm:text-4xl mb-4">Built Compliance-First</h2>
        <p className="text-subink max-w-3xl mb-8">
          Compliance is built into every lead we deliver. Each submission
          carries documented TCPA consent, TrustedForm certification where
          applicable, and structured intake data — giving your firm a defensible
          record and a clear picture of the prospect before you make contact.
        </p>
        <div className="border-t border-b border-hairline py-5">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-subink">
            {["TrustedForm", "ActiveProspect", "Google Ads", "Meta"].map(
              (p, i) => (
                <React.Fragment key={p}>
                  {i > 0 && <span className="text-hairline">·</span>}
                  <span data-testid={`compliance-platform-${i}`}>{p}</span>
                </React.Fragment>
              )
            )}
          </div>
        </div>
      </Section>

      {/* g) FAQ */}
      <Section id="faq" data-testid="faq-section">
        <h2 className="text-3xl sm:text-4xl mb-8">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="max-w-3xl">
          {FAQS.map((faq, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border-b border-hairline"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="text-left text-ink font-semibold text-base hover:no-underline">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-subink text-[15px] leading-relaxed">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* h) CONTACT / SIGN UP */}
      <Section id="contact" alt data-testid="home-contact-section">
        <h2 className="text-3xl sm:text-4xl mb-3">Get Started</h2>
        <p className="text-subink max-w-2xl mb-8">
          Tell us about your practice and the leads you're looking for. We'll
          follow up with pricing and current availability for your practice area
          and geography.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-2">
            <ContactForm sourcePage="home" />
          </div>
          <div className="hidden lg:block">
            <img
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="A firm and a new client shaking hands"
              className="w-full h-full min-h-[300px] object-cover rounded-[4px] border border-hairline"
              data-testid="contact-image"
            />
          </div>
        </div>
      </Section>
    </>
  );
}
